'use client';

import { useState, useCallback } from 'react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type WorkStyle = 'Remote' | 'Hybrid' | 'Office';

interface AnswerOption {
  text: string;
  scores: Record<WorkStyle, number>;
}

interface Question {
  id: number;
  question: string;
  subtitle: string;
  icon: string;
  options: AnswerOption[];
}

interface WorkStyleProfile {
  name: string;
  tagline: string;
  description: string;
  pros: string[];
  cons: string[];
  tips: string[];
  color: string;
  bgGradient: string;
  lightBg: string;
  border: string;
  textColor: string;
  icon: string;
}

interface QuizResult {
  primary: WorkStyle;
  scores: Record<WorkStyle, number>;
  maxPossible: number;
  personalizedPros: Record<WorkStyle, string[]>;
  personalizedCons: Record<WorkStyle, string[]>;
}

/* ------------------------------------------------------------------ */
/*  Work style profiles                                                */
/* ------------------------------------------------------------------ */

const WORK_STYLE_PROFILES: Record<WorkStyle, WorkStyleProfile> = {
  Remote: {
    name: 'Remote Work',
    tagline: 'You thrive with autonomy, focus, and freedom.',
    description:
      'You are at your best when you have full control over your environment, schedule, and workflow. You are self-disciplined, tech-savvy, and energized by deep, uninterrupted focus time. You do not need the buzz of an office to stay motivated -- your drive comes from within. Remote work unlocks your highest productivity and lets you design a work life that fits your life.',
    pros: [
      'Zero commute means more time for life and deep work',
      'Full control over your workspace and environment',
      'Fewer interruptions and more focus time',
      'Geographic freedom to live anywhere',
      'Flexible schedule for peak productivity',
      'Reduced expenses on commuting and work wardrobe',
    ],
    cons: [
      'Can feel isolating without intentional social effort',
      'Work-life boundaries can blur without discipline',
      'Career visibility may require extra self-advocacy',
      'Collaboration across time zones can be tricky',
      'Home distractions (kids, chores, deliveries) require management',
    ],
    tips: [
      'Create a dedicated workspace with a door you can close',
      'Set hard start and stop times to protect work-life boundaries',
      'Schedule virtual coffee chats to maintain team connections',
      'Over-communicate on Slack and in async docs -- visibility matters',
      'Take real breaks: walk outside, exercise, step away from screens',
      'Invest in quality equipment: monitor, chair, webcam, and lighting',
      'Join a coworking space one or two days a week if isolation creeps in',
    ],
    color: 'from-emerald-500 to-green-600',
    bgGradient: 'bg-gradient-to-r from-emerald-500 to-green-600',
    lightBg: 'bg-emerald-50',
    border: 'border-emerald-200',
    textColor: 'text-emerald-700',
    icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
  },
  Hybrid: {
    name: 'Hybrid Work',
    tagline: 'You want the best of both worlds.',
    description:
      'You are adaptable, socially aware, and strategic about when you need focus time versus collaboration time. You value in-person connection but do not want it every day. Hybrid work gives you the flexibility to match your environment to the task at hand -- heads-down work at home, brainstorming and bonding in the office. You are the type who can thrive anywhere, as long as you have choice.',
    pros: [
      'Flexibility to match environment to the task',
      'In-person collaboration when it matters most',
      'Focus time at home for deep work',
      'Maintains social connections and company culture',
      'Reduced (but not eliminated) commute time',
      'Better work-life balance than full-time office',
    ],
    cons: [
      'Requires strong planning and calendar management',
      'Can feel like you are never fully "in" either world',
      'Office days may fill up with back-to-back meetings',
      'Need to maintain two workspaces (home and office)',
      'Team coordination is harder with varying schedules',
    ],
    tips: [
      'Batch meetings and collaborative work on office days',
      'Reserve home days for deep focus and creative work',
      'Keep your home workspace just as professional as your office setup',
      'Coordinate in-office days with your closest collaborators',
      'Use office time for relationship building, not just meetings',
      'Communicate your schedule clearly so teammates know when to reach you',
      'Protect at least two home days per week for uninterrupted focus',
    ],
    color: 'from-blue-500 to-indigo-600',
    bgGradient: 'bg-gradient-to-r from-blue-500 to-indigo-600',
    lightBg: 'bg-blue-50',
    border: 'border-blue-200',
    textColor: 'text-blue-700',
    icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  },
  Office: {
    name: 'Office Work',
    tagline: 'You are energized by people, structure, and presence.',
    description:
      'You do your best work when you are surrounded by teammates, have a clear structure to your day, and can collaborate in real time. You feed off the energy of a shared space and find that spontaneous conversations, whiteboard sessions, and in-person mentorship are where the magic happens. An office gives you the social fuel, routine, and clear separation between work and home that keeps you performing at your peak.',
    pros: [
      'Clear separation between work and personal life',
      'Immediate access to teammates for quick collaboration',
      'Structured routine that reduces decision fatigue',
      'Stronger social connections and company culture',
      'Easier mentorship and career development visibility',
      'Dedicated professional workspace and equipment',
    ],
    cons: [
      'Daily commute consumes time and energy',
      'Open offices can be noisy and distracting',
      'Less schedule flexibility for personal errands',
      'Geographic limitation on where you can live',
      'Higher costs for commuting, meals, and wardrobe',
    ],
    tips: [
      'Use noise-canceling headphones for focus blocks in open offices',
      'Block "no meeting" time on your calendar for deep work',
      'Use commute time productively: podcasts, audiobooks, or planning',
      'Build relationships across teams -- proximity is your advantage',
      'Advocate for flexible hours even in office-first cultures',
      'Take advantage of in-person mentorship opportunities',
      'Step outside for breaks -- do not let the office become a cage',
    ],
    color: 'from-purple-500 to-violet-600',
    bgGradient: 'bg-gradient-to-r from-purple-500 to-violet-600',
    lightBg: 'bg-purple-50',
    border: 'border-purple-200',
    textColor: 'text-purple-700',
    icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
  },
};

/* ------------------------------------------------------------------ */
/*  Quiz questions (12)                                                */
/* ------------------------------------------------------------------ */

const QUESTIONS: Question[] = [
  {
    id: 1,
    question: 'How disciplined are you about staying productive without a manager watching?',
    subtitle: 'Self-discipline',
    icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
    options: [
      { text: 'Extremely disciplined -- I create my own structure and stick to it', scores: { Remote: 3, Hybrid: 1, Office: 0 } },
      { text: 'Pretty good, but I sometimes need external accountability', scores: { Remote: 1, Hybrid: 3, Office: 1 } },
      { text: 'I am most productive when I know others are around and working too', scores: { Remote: 0, Hybrid: 1, Office: 3 } },
      { text: 'I honestly do my best work with a structured schedule and oversight', scores: { Remote: 0, Hybrid: 1, Office: 3 } },
    ],
  },
  {
    id: 2,
    question: 'How do you prefer to communicate with coworkers?',
    subtitle: 'Communication preference',
    icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
    options: [
      { text: 'Async messages and docs -- I like thinking before responding', scores: { Remote: 3, Hybrid: 1, Office: 0 } },
      { text: 'A mix of chat, video calls, and occasional in-person meetings', scores: { Remote: 1, Hybrid: 3, Office: 1 } },
      { text: 'Quick in-person conversations -- I can read body language and energy', scores: { Remote: 0, Hybrid: 1, Office: 3 } },
      { text: 'Scheduled meetings with clear agendas, whether virtual or in-person', scores: { Remote: 1, Hybrid: 2, Office: 2 } },
    ],
  },
  {
    id: 3,
    question: 'How important is daily social interaction with colleagues?',
    subtitle: 'Social needs',
    icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
    options: [
      { text: 'Not important -- I recharge alone and have a rich social life outside work', scores: { Remote: 3, Hybrid: 1, Office: 0 } },
      { text: 'I like some interaction but do not need it every day', scores: { Remote: 1, Hybrid: 3, Office: 0 } },
      { text: 'Pretty important -- I enjoy casual chats and team energy', scores: { Remote: 0, Hybrid: 2, Office: 3 } },
      { text: 'Essential -- my best ideas come from spontaneous hallway conversations', scores: { Remote: 0, Hybrid: 1, Office: 3 } },
    ],
  },
  {
    id: 4,
    question: 'What does your home workspace situation look like?',
    subtitle: 'Home workspace',
    icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
    options: [
      { text: 'Dedicated home office with a door, great setup, and zero interruptions', scores: { Remote: 3, Hybrid: 2, Office: 0 } },
      { text: 'Decent setup at a desk or table -- it works but is not perfect', scores: { Remote: 1, Hybrid: 3, Office: 1 } },
      { text: 'I work from the couch or kitchen table -- no dedicated space', scores: { Remote: 0, Hybrid: 1, Office: 3 } },
      { text: 'Noisy house with roommates, kids, or pets -- focus is a struggle', scores: { Remote: 0, Hybrid: 1, Office: 3 } },
    ],
  },
  {
    id: 5,
    question: 'How do you feel about commuting to work?',
    subtitle: 'Commute tolerance',
    icon: 'M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0',
    options: [
      { text: 'I despise commuting -- that time is completely wasted', scores: { Remote: 3, Hybrid: 1, Office: 0 } },
      { text: 'I do not love it, but a couple of days a week is fine', scores: { Remote: 1, Hybrid: 3, Office: 0 } },
      { text: 'I do not mind it -- I use it as transition time to mentally shift gears', scores: { Remote: 0, Hybrid: 2, Office: 3 } },
      { text: 'I actually enjoy it -- podcasts, music, or just quiet thinking time', scores: { Remote: 0, Hybrid: 1, Office: 3 } },
    ],
  },
  {
    id: 6,
    question: 'How do you prefer to collaborate on projects?',
    subtitle: 'Collaboration style',
    icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
    options: [
      { text: 'Shared docs, async feedback, and recorded walkthroughs', scores: { Remote: 3, Hybrid: 1, Office: 0 } },
      { text: 'Quick video calls mixed with async work depending on what is needed', scores: { Remote: 1, Hybrid: 3, Office: 1 } },
      { text: 'Whiteboard sessions and in-person brainstorms -- energy matters', scores: { Remote: 0, Hybrid: 1, Office: 3 } },
      { text: 'Whatever works, but I like sitting next to teammates for fast iteration', scores: { Remote: 0, Hybrid: 2, Office: 3 } },
    ],
  },
  {
    id: 7,
    question: 'How do you manage your time and prioritize tasks?',
    subtitle: 'Time management',
    icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    options: [
      { text: 'I use personal systems (calendars, Notion, time-blocking) and self-manage', scores: { Remote: 3, Hybrid: 1, Office: 0 } },
      { text: 'I am good at self-managing but value check-ins to stay aligned', scores: { Remote: 1, Hybrid: 3, Office: 1 } },
      { text: 'I rely on team standups and shared schedules to keep me on track', scores: { Remote: 0, Hybrid: 2, Office: 3 } },
      { text: 'I work best with a clear daily routine set by my team or manager', scores: { Remote: 0, Hybrid: 1, Office: 3 } },
    ],
  },
  {
    id: 8,
    question: 'What is your biggest distraction challenge?',
    subtitle: 'Distraction handling',
    icon: 'M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636',
    options: [
      { text: 'Open offices and chatty coworkers -- I need quiet to focus', scores: { Remote: 3, Hybrid: 2, Office: 0 } },
      { text: 'It depends on the day -- sometimes home, sometimes office is worse', scores: { Remote: 1, Hybrid: 3, Office: 1 } },
      { text: 'Working from home -- I get tempted by chores, TV, or the fridge', scores: { Remote: 0, Hybrid: 1, Office: 3 } },
      { text: 'Being alone -- when nobody is around I lose focus and motivation', scores: { Remote: 0, Hybrid: 1, Office: 3 } },
    ],
  },
  {
    id: 9,
    question: 'How comfortable are you with remote work technology?',
    subtitle: 'Tech comfort',
    icon: 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
    options: [
      { text: 'Power user -- I live in Slack, Notion, Zoom, and async video tools', scores: { Remote: 3, Hybrid: 2, Office: 0 } },
      { text: 'Comfortable with all the tools, though I prefer some things in person', scores: { Remote: 1, Hybrid: 3, Office: 1 } },
      { text: 'I can use them but find virtual collaboration draining and impersonal', scores: { Remote: 0, Hybrid: 1, Office: 3 } },
      { text: 'I struggle with remote tools and prefer face-to-face communication', scores: { Remote: 0, Hybrid: 1, Office: 3 } },
    ],
  },
  {
    id: 10,
    question: 'How do you prefer to separate work from personal life?',
    subtitle: 'Work-life boundaries',
    icon: 'M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3',
    options: [
      { text: 'I am great at shutting the laptop and switching off -- no office needed', scores: { Remote: 3, Hybrid: 1, Office: 0 } },
      { text: 'I can manage it but having some office days helps me draw the line', scores: { Remote: 1, Hybrid: 3, Office: 1 } },
      { text: 'I need a physical change of scenery to truly disconnect from work', scores: { Remote: 0, Hybrid: 2, Office: 3 } },
      { text: 'When I work from home, I end up working all evening -- I need the office', scores: { Remote: 0, Hybrid: 1, Office: 3 } },
    ],
  },
  {
    id: 11,
    question: 'What is your ideal meeting setup?',
    subtitle: 'Meeting preferences',
    icon: 'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z',
    options: [
      { text: 'As few meetings as possible -- replace them with async updates', scores: { Remote: 3, Hybrid: 1, Office: 0 } },
      { text: 'Important meetings in person, routine check-ins over video', scores: { Remote: 1, Hybrid: 3, Office: 1 } },
      { text: 'Regular face-to-face meetings -- I read the room better in person', scores: { Remote: 0, Hybrid: 1, Office: 3 } },
      { text: 'Daily standups and frequent in-person syncs keep me aligned and motivated', scores: { Remote: 0, Hybrid: 1, Office: 3 } },
    ],
  },
  {
    id: 12,
    question: 'When do you do your best, most creative work?',
    subtitle: 'Productivity environment',
    icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
    options: [
      { text: 'Early morning or late night at home when the world is quiet', scores: { Remote: 3, Hybrid: 1, Office: 0 } },
      { text: 'It varies -- some days at home, some days the office energy helps', scores: { Remote: 1, Hybrid: 3, Office: 1 } },
      { text: 'In a coffee-shop-like buzz where I can hear activity around me', scores: { Remote: 0, Hybrid: 2, Office: 3 } },
      { text: 'In the office after a great team brainstorm gets my ideas flowing', scores: { Remote: 0, Hybrid: 1, Office: 3 } },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Personalized pros/cons based on answer patterns                    */
/* ------------------------------------------------------------------ */

function getPersonalizedInsights(answers: Record<number, number>): {
  pros: Record<WorkStyle, string[]>;
  cons: Record<WorkStyle, string[]>;
} {
  const pros: Record<WorkStyle, string[]> = { Remote: [], Hybrid: [], Office: [] };
  const cons: Record<WorkStyle, string[]> = { Remote: [], Hybrid: [], Office: [] };

  // Q1: Self-discipline
  const q1 = answers[1];
  if (q1 === 0) {
    pros.Remote.push('Your strong self-discipline is a superpower for remote work');
    cons.Office.push('A structured office may feel unnecessarily constraining for you');
  } else if (q1 === 2 || q1 === 3) {
    pros.Office.push('The built-in accountability of office life matches your work style');
    cons.Remote.push('Without external structure, you might struggle to stay on track');
  }

  // Q3: Social needs
  const q3 = answers[3];
  if (q3 === 0) {
    pros.Remote.push('You do not depend on workplace socializing to stay fulfilled');
    cons.Office.push('Daily office socializing might drain your energy');
  } else if (q3 === 2 || q3 === 3) {
    pros.Office.push('You will get the daily human interaction that fuels your energy');
    cons.Remote.push('Working alone all day may leave you feeling isolated and unmotivated');
  }

  // Q4: Home workspace
  const q4 = answers[4];
  if (q4 === 0) {
    pros.Remote.push('Your dedicated home office sets you up perfectly for remote work');
  } else if (q4 === 2 || q4 === 3) {
    pros.Office.push('A professional office gives you the workspace your home lacks');
    cons.Remote.push('Without a proper home workspace, remote productivity will suffer');
  }

  // Q5: Commute tolerance
  const q5 = answers[5];
  if (q5 === 0) {
    pros.Remote.push('You will reclaim hours of commute time for work or personal life');
    cons.Office.push('A daily commute would be a major energy and time drain for you');
  } else if (q5 === 2 || q5 === 3) {
    pros.Office.push('You value the commute as a mental transition between work and life');
  }

  // Q8: Distraction handling
  const q8 = answers[8];
  if (q8 === 0) {
    pros.Remote.push('Escaping open-office noise will unlock your best focus');
    cons.Office.push('Office distractions are your biggest productivity killer');
  } else if (q8 === 2 || q8 === 3) {
    pros.Office.push('The office environment keeps you from home distractions');
    cons.Remote.push('Home distractions will be a real challenge for your productivity');
  }

  // Q9: Tech comfort
  const q9 = answers[9];
  if (q9 === 0) {
    pros.Remote.push('Your tech fluency makes async and remote collaboration seamless');
  } else if (q9 === 2 || q9 === 3) {
    pros.Office.push('In-person communication plays to your strengths');
    cons.Remote.push('Remote tools may feel impersonal and frustrating for you');
  }

  // Q10: Work-life boundaries
  const q10 = answers[10];
  if (q10 === 0) {
    pros.Remote.push('Your strong boundaries mean remote work will not invade your personal life');
  } else if (q10 === 2 || q10 === 3) {
    pros.Office.push('Leaving the office gives you the clean break you need');
    cons.Remote.push('Without a physical separation, you risk working around the clock');
  }

  // Hybrid always gets balanced pros
  if (pros.Hybrid.length === 0) {
    pros.Hybrid.push(
      'You get flexibility to match your work environment to the task',
      'In-person days satisfy your social needs without a daily commute',
      'Home days give you the focus time you need for deep work',
    );
  }
  if (cons.Hybrid.length === 0) {
    cons.Hybrid.push(
      'Switching between two environments requires strong planning skills',
      'You may feel you never fully belong in either the remote or office camp',
    );
  }

  // Ensure minimums
  for (const style of ['Remote', 'Hybrid', 'Office'] as WorkStyle[]) {
    if (pros[style].length === 0) {
      pros[style].push(...WORK_STYLE_PROFILES[style].pros.slice(0, 2));
    }
    if (cons[style].length === 0) {
      cons[style].push(...WORK_STYLE_PROFILES[style].cons.slice(0, 2));
    }
  }

  return { pros, cons };
}

/* ------------------------------------------------------------------ */
/*  Scoring logic                                                      */
/* ------------------------------------------------------------------ */

function computeResults(answers: Record<number, number>): QuizResult {
  const scores: Record<WorkStyle, number> = { Remote: 0, Hybrid: 0, Office: 0 };

  for (const question of QUESTIONS) {
    const selectedIndex = answers[question.id];
    if (selectedIndex === undefined) continue;
    const option = question.options[selectedIndex];
    for (const [style, pts] of Object.entries(option.scores)) {
      scores[style as WorkStyle] += pts;
    }
  }

  const sorted = (Object.entries(scores) as [WorkStyle, number][]).sort(
    (a, b) => b[1] - a[1],
  );
  const primary = sorted[0][0];

  // Max possible is 12 questions * 3 points max = 36
  const maxPossible = 36;

  const { pros, cons } = getPersonalizedInsights(answers);

  return { primary, scores, maxPossible, personalizedPros: pros, personalizedCons: cons };
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

function ScoreBar({
  style,
  score,
  maxPossible,
  isPrimary,
}: {
  style: WorkStyle;
  score: number;
  maxPossible: number;
  isPrimary: boolean;
}) {
  const pct = maxPossible > 0 ? Math.round((score / maxPossible) * 100) : 0;
  const colors: Record<WorkStyle, string> = {
    Remote: 'bg-emerald-500',
    Hybrid: 'bg-blue-500',
    Office: 'bg-purple-500',
  };
  const labels: Record<WorkStyle, string> = {
    Remote: 'Remote',
    Hybrid: 'Hybrid',
    Office: 'Office',
  };

  return (
    <div className="flex items-center gap-3">
      <span
        className={`w-20 text-sm font-semibold shrink-0 ${
          isPrimary ? 'font-extrabold text-gray-900' : 'text-gray-600'
        }`}
      >
        {labels[style]}
      </span>
      <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-1000 ease-out ${colors[style]}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="w-16 text-sm font-bold text-gray-600 text-right">
        {score}/{maxPossible}
      </span>
    </div>
  );
}

function WorkStyleCard({
  style,
  pros,
  cons,
  isPrimary,
}: {
  style: WorkStyle;
  pros: string[];
  cons: string[];
  isPrimary: boolean;
}) {
  const profile = WORK_STYLE_PROFILES[style];
  return (
    <div
      className={`rounded-2xl border-2 overflow-hidden ${
        isPrimary ? `${profile.border} shadow-lg` : 'border-gray-200'
      }`}
    >
      <div className={`px-6 py-4 ${isPrimary ? profile.lightBg : 'bg-gray-50'}`}>
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-xl bg-gradient-to-br ${profile.color} flex items-center justify-center`}
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={profile.icon} />
            </svg>
          </div>
          <div>
            <h4 className="font-extrabold text-gray-900">{profile.name}</h4>
            {isPrimary && (
              <span className={`text-xs font-bold ${profile.textColor}`}>Best Match</span>
            )}
          </div>
        </div>
      </div>
      <div className="px-6 py-5 space-y-4">
        <div>
          <p className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-2">
            Pros for you
          </p>
          <ul className="space-y-2">
            {pros.map((p, i) => (
              <li key={i} className="flex items-start gap-2">
                <svg className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm text-gray-700 leading-relaxed">{p}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-2">
            Watch out for
          </p>
          <ul className="space-y-2">
            {cons.map((c, i) => (
              <li key={i} className="flex items-start gap-2">
                <svg className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01" />
                </svg>
                <span className="text-sm text-gray-700 leading-relaxed">{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export default function RemoteWorkQuizContent() {
  const [currentQuestion, setCurrentQuestion] = useState(-1);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [result, setResult] = useState<QuizResult | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);

  const totalQuestions = QUESTIONS.length;
  const progress =
    currentQuestion >= 0
      ? Math.round(((currentQuestion + 1) / totalQuestions) * 100)
      : 0;

  const handleStartQuiz = useCallback(() => {
    setCurrentQuestion(0);
    setAnswers({});
    setResult(null);
    setSelectedOption(null);
  }, []);

  const handleSelectAnswer = useCallback(
    (optionIndex: number) => {
      setSelectedOption(optionIndex);

      setTimeout(() => {
        const questionId = QUESTIONS[currentQuestion].id;
        const newAnswers = { ...answers, [questionId]: optionIndex };
        setAnswers(newAnswers);
        setSelectedOption(null);

        if (currentQuestion < totalQuestions - 1) {
          setCurrentQuestion(currentQuestion + 1);
        } else {
          setIsCalculating(true);
          setTimeout(() => {
            setResult(computeResults(newAnswers));
            setIsCalculating(false);
          }, 1500);
        }
      }, 400);
    },
    [currentQuestion, answers, totalQuestions],
  );

  const handleRetake = useCallback(() => {
    setCurrentQuestion(-1);
    setAnswers({});
    setResult(null);
    setSelectedOption(null);
  }, []);

  const optionLetters = ['A', 'B', 'C', 'D'];
  const optionHoverColors = [
    'hover:border-emerald-400 hover:bg-emerald-50 focus:border-emerald-400 focus:bg-emerald-50',
    'hover:border-blue-400 hover:bg-blue-50 focus:border-blue-400 focus:bg-blue-50',
    'hover:border-purple-400 hover:bg-purple-50 focus:border-purple-400 focus:bg-purple-50',
    'hover:border-amber-400 hover:bg-amber-50 focus:border-amber-400 focus:bg-amber-50',
  ];
  const optionSelectedColors = [
    'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200',
    'border-blue-500 bg-blue-50 ring-2 ring-blue-200',
    'border-purple-500 bg-purple-50 ring-2 ring-purple-200',
    'border-amber-500 bg-amber-50 ring-2 ring-amber-200',
  ];
  const letterBgColors = [
    'bg-emerald-500',
    'bg-blue-500',
    'bg-purple-500',
    'bg-amber-500',
  ];

  // Progress bar gradient based on dominant style so far
  const getProgressGradient = () => {
    if (currentQuestion < 0) return 'from-emerald-400 via-blue-500 to-purple-500';
    const tempScores: Record<WorkStyle, number> = { Remote: 0, Hybrid: 0, Office: 0 };
    for (const question of QUESTIONS) {
      const idx = answers[question.id];
      if (idx === undefined) continue;
      for (const [s, pts] of Object.entries(question.options[idx].scores)) {
        tempScores[s as WorkStyle] += pts;
      }
    }
    const top = (Object.entries(tempScores) as [WorkStyle, number][]).sort(
      (a, b) => b[1] - a[1],
    )[0][0];
    if (top === 'Remote') return 'from-emerald-400 to-green-500';
    if (top === 'Hybrid') return 'from-blue-400 to-indigo-500';
    return 'from-purple-400 to-violet-500';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-purple-50">
      <ToolHero
        title="Remote Work Quiz"
        description="Find out if remote, hybrid, or office work is best for you. 12 questions, zero sign-ups, instant results."
        category="Career Exploration"
      />

      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* ============================================================ */}
        {/*  INTRO SCREEN                                                 */}
        {/* ============================================================ */}
        {currentQuestion === -1 && !result && (
          <div className="space-y-8">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-400 via-blue-500 to-purple-500 flex items-center justify-center">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-extrabold text-gray-900 mb-3">
                Where Do You Do Your Best Work?
              </h2>
              <p className="text-gray-600 max-w-lg mx-auto mb-8 leading-relaxed">
                Remote, hybrid, or office? The answer is different for everyone. Take this quick quiz to discover which work arrangement matches your personality, habits, and preferences -- and get personalized tips to thrive in it.
              </p>

              <div className="grid grid-cols-3 gap-4 max-w-md mx-auto mb-8">
                <div className="text-center">
                  <p className="text-3xl font-extrabold text-emerald-600">12</p>
                  <p className="text-xs text-gray-500 font-medium">Questions</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-extrabold text-blue-600">2 min</p>
                  <p className="text-xs text-gray-500 font-medium">To Complete</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-extrabold text-purple-600">3</p>
                  <p className="text-xs text-gray-500 font-medium">Work Styles</p>
                </div>
              </div>

              <button
                onClick={handleStartQuiz}
                className="bg-gradient-to-r from-emerald-500 via-blue-600 to-purple-600 text-white font-bold py-4 px-10 rounded-xl shadow-lg hover:from-emerald-600 hover:via-blue-700 hover:to-purple-700 transition-all text-lg cursor-pointer"
              >
                Start the Quiz
              </button>
              <p className="text-xs text-gray-400 mt-4">
                100% free. No sign-up required. Everything runs in your browser.
              </p>
            </div>

            {/* Three work styles preview */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                Three Work Styles We Evaluate
              </h3>
              <div className="grid sm:grid-cols-3 gap-4">
                {(['Remote', 'Hybrid', 'Office'] as WorkStyle[]).map((style) => {
                  const profile = WORK_STYLE_PROFILES[style];
                  return (
                    <div
                      key={style}
                      className="flex flex-col items-center text-center p-5 rounded-xl border border-gray-100 hover:border-gray-200 transition-colors"
                    >
                      <div
                        className={`w-12 h-12 rounded-xl bg-gradient-to-br ${profile.color} flex items-center justify-center mb-3`}
                      >
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={profile.icon} />
                        </svg>
                      </div>
                      <p className="font-bold text-gray-900 text-sm">{profile.name}</p>
                      <p className="text-xs text-gray-500 mt-1">{profile.tagline}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            <ToolCTA
              headline="Ready to land your next role?"
              description="Superpower Resume helps you build an ATS-optimized resume that gets interviews -- whether you are targeting remote, hybrid, or office roles."
              buttonText="Build My Resume"
            />
          </div>
        )}

        {/* ============================================================ */}
        {/*  QUIZ QUESTIONS                                               */}
        {/* ============================================================ */}
        {currentQuestion >= 0 && !result && !isCalculating && (
          <div>
            {/* Progress bar */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-gray-600">
                  Question {currentQuestion + 1} of {totalQuestions}
                </span>
                <span className="text-sm font-bold text-gray-800">{progress}%</span>
              </div>
              <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full bg-gradient-to-r ${getProgressGradient()} rounded-full transition-all duration-500 ease-out`}
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Question card */}
            {(() => {
              const q = QUESTIONS[currentQuestion];
              return (
                <div
                  key={q.id}
                  className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8"
                >
                  {/* Category and icon */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 via-blue-500 to-purple-500 flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={q.icon} />
                      </svg>
                    </div>
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                      {q.subtitle}
                    </span>
                  </div>

                  <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900 mb-8 leading-tight">
                    {q.question}
                  </h2>

                  {/* Options */}
                  <div className="space-y-3">
                    {q.options.map((option, idx) => {
                      const isSelected = selectedOption === idx;
                      return (
                        <button
                          key={idx}
                          onClick={() => handleSelectAnswer(idx)}
                          disabled={selectedOption !== null}
                          className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer flex items-start gap-4 ${
                            isSelected
                              ? optionSelectedColors[idx]
                              : `border-gray-200 bg-white ${optionHoverColors[idx]}`
                          } ${selectedOption !== null && !isSelected ? 'opacity-50' : ''}`}
                        >
                          <span
                            className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-extrabold shrink-0 transition-colors ${
                              isSelected
                                ? `${letterBgColors[idx]} text-white`
                                : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            {optionLetters[idx]}
                          </span>
                          <span className="text-sm sm:text-base font-medium text-gray-800 leading-relaxed pt-1">
                            {option.text}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* ============================================================ */}
        {/*  CALCULATING SCREEN                                           */}
        {/* ============================================================ */}
        {isCalculating && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-400 via-blue-500 to-purple-500 flex items-center justify-center animate-pulse">
              <svg className="w-8 h-8 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
            <h2 className="text-2xl font-extrabold text-gray-900 mb-2">
              Analyzing your work style...
            </h2>
            <p className="text-gray-500">
              Mapping your responses to find your ideal work arrangement.
            </p>
          </div>
        )}

        {/* ============================================================ */}
        {/*  RESULTS                                                      */}
        {/* ============================================================ */}
        {result && (
          <div className="space-y-8">
            {/* Primary result hero */}
            {(() => {
              const profile = WORK_STYLE_PROFILES[result.primary];
              return (
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                  <div
                    className={`${profile.bgGradient} px-8 py-10 text-white text-center`}
                  >
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={profile.icon} />
                      </svg>
                    </div>
                    <p className="text-sm font-bold text-white/80 uppercase tracking-wider mb-1">
                      Your Best Work Arrangement
                    </p>
                    <h2 className="text-3xl sm:text-4xl font-extrabold mb-2">
                      {profile.name}
                    </h2>
                    <p className="text-lg text-white/90 font-medium">{profile.tagline}</p>
                  </div>
                  <div className="px-8 py-6">
                    <p className="text-gray-700 leading-relaxed">{profile.description}</p>
                  </div>
                </div>
              );
            })()}

            {/* Score breakdown */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                Your Score Breakdown
              </h3>
              <div className="space-y-4">
                {(['Remote', 'Hybrid', 'Office'] as WorkStyle[])
                  .sort((a, b) => result.scores[b] - result.scores[a])
                  .map((style) => (
                    <ScoreBar
                      key={style}
                      style={style}
                      score={result.scores[style]}
                      maxPossible={result.maxPossible}
                      isPrimary={style === result.primary}
                    />
                  ))}
              </div>
              <p className="text-xs text-gray-400 mt-4">
                Each question awards up to 3 points to the most aligned work style.
              </p>
            </div>

            {/* Personalized pros/cons for each style */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Personalized Pros and Cons
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                Based on your specific answers, here is how each work style would play out for you.
              </p>
              <div className="space-y-6">
                {(['Remote', 'Hybrid', 'Office'] as WorkStyle[])
                  .sort((a, b) => {
                    if (a === result.primary) return -1;
                    if (b === result.primary) return 1;
                    return result.scores[b] - result.scores[a];
                  })
                  .map((style) => (
                    <WorkStyleCard
                      key={style}
                      style={style}
                      pros={result.personalizedPros[style]}
                      cons={result.personalizedCons[style]}
                      isPrimary={style === result.primary}
                    />
                  ))}
              </div>
            </div>

            {/* Tips for recommended style */}
            {(() => {
              const profile = WORK_STYLE_PROFILES[result.primary];
              return (
                <div className={`${profile.lightBg} rounded-2xl border ${profile.border} p-8`}>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-xl bg-gradient-to-br ${profile.color} flex items-center justify-center`}
                    >
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    Tips to Thrive in {profile.name}
                  </h3>
                  <p className="text-sm text-gray-500 mb-6 ml-13">
                    Actionable advice based on your results.
                  </p>
                  <ul className="space-y-3">
                    {profile.tips.map((tip, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className={`w-6 h-6 rounded-full ${profile.lightBg} border ${profile.border} flex items-center justify-center text-xs font-bold ${profile.textColor} shrink-0 mt-0.5`}>
                          {i + 1}
                        </span>
                        <span className="text-sm text-gray-700 leading-relaxed">
                          {tip}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
                    <strong>Sources &amp; Further Reading:</strong>{' '}
                    <a href="https://www.bls.gov/ooh/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">BLS Occupational Outlook Handbook &ndash; Work Environment Data</a>
                    {' · '}
                    <a href="https://www.careeronestop.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">CareerOneStop &ndash; U.S. DOL Workplace &amp; Career Resources</a>
                    {' · '}
                    <a href="https://hbr.org/topic/career-planning" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Harvard Business Review &ndash; Remote Work &amp; Career Planning Research</a>
                  </p>
                </div>
              );
            })()}

            {/* Actions: Retake */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={handleRetake}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-500 via-blue-600 to-purple-600 text-white font-bold rounded-xl shadow-lg hover:from-emerald-600 hover:via-blue-700 hover:to-purple-700 transition-all cursor-pointer"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Retake Quiz
              </button>
            </div>

            {/* CTA */}
            <ToolCTA
              headline="Know your work style? Build a resume that lands the role."
              description="Superpower Resume creates ATS-optimized resumes tailored to remote, hybrid, or office positions. Start for free in minutes."
              buttonText="Build My Resume"
            />
          </div>
        )}
      </div>
    </div>
  );
}
